import styled from '@emotion/styled';
import React, { Ref, forwardRef, memo, FocusEvent, ChangeEvent } from 'react';
import { isNull } from 'lodash';
import { InputNumberProps as $InputNumberProps } from 'antd/lib/input-number';
import {
  InputProps as $InputProps,
  PasswordProps as $PasswordProps,
  TextAreaProps as $TextAreaProps,
  InputRef,
} from 'antd/lib/input';

import { OnBlurCallback, OnChangeCallback } from '.';

import theme from '../../theme';
import { Input as $Input, InputNumber as $InputNumber } from '../common/antd/antd-override';

export type InputElementProps = {
  inputType?: string;
  onBlur?: OnBlurCallback;
  onChange?: OnChangeCallback;
};

export type TextAreaProps = Omit<$TextAreaProps, 'allowClear' | 'onBlur' | 'onChange'> & InputElementProps;
export type PasswordProps = Omit<$PasswordProps, 'onBlur' | 'onChange'> & InputElementProps;

export type InputNumberProps = Omit<$InputNumberProps, 'type' | 'onBlur' | 'onChange'> & InputElementProps;
export type InputProps = Omit<$InputProps, 'type' | 'allowClear' | 'onBlur' | 'onChange'> & InputElementProps;

const ForwardRefPassword = forwardRef<InputRef, PasswordProps>(function InputPassword(
  { name, onBlur, onChange, ...props },
  ref
) {
  const handleBlur =
    onBlur &&
    ((event: FocusEvent<HTMLInputElement>) => {
      const {
        target: { value },
      } = event;
      onBlur({ name, value });
    });
  const handleChange =
    onChange &&
    ((event: ChangeEvent<HTMLInputElement>) => {
      const {
        target: { value, selectionStart },
      } = event;
      onChange({
        name,
        value,
        selectionStart: isNull(selectionStart) ? 0 : selectionStart,
      });
    });
  return <$Input.Password {...props} name={name} onBlur={handleBlur} onChange={handleChange} ref={ref} />;
});

export const InputPassword = memo(ForwardRefPassword);

export const TextArea = memo(function TextArea({
  name,
  autoSize = { minRows: 3, maxRows: 3 },
  onBlur,
  onChange,
  ...props
}: TextAreaProps) {
  const handleBlur =
    onBlur &&
    ((event: FocusEvent<HTMLTextAreaElement>) => {
      const {
        target: { value },
      } = event;
      onBlur({ name, value });
    });
  const handleChange =
    onChange &&
    ((event: ChangeEvent<HTMLTextAreaElement>) => {
      const {
        target: { value },
      } = event;
      onChange({ name, value });
    });
  return <$Input.TextArea {...props} name={name} autoSize={autoSize} onBlur={handleBlur} onChange={handleChange} />;
});

const ForwardRefInput = forwardRef(function Input(initProps: any, ref: Ref<typeof $Input>) {
  if (initProps.inputType === 'number') {
    const { name, onBlur, onChange, ...props }: InputNumberProps = initProps;
    const handleBlur =
      onBlur &&
      ((event: React.FocusEvent<HTMLInputElement>) => {
        const {
          target: { value },
        } = event;
        onBlur({ name, value: Number(value) });
      });
    const handleChange =
      onChange &&
      ((value?: number) => {
        onChange({ name, value });
      });
    return <$InputNumber {...props} name={name} onBlur={handleBlur} onChange={handleChange as any} />;
  } else {
    const { inputType, name, onBlur, onChange, ...props }: InputProps = initProps;
    const getValue = (value: any) => {
      let data: any = value;
      if (data === '') {
        data = undefined;
      } else if (inputType === 'number' && !isNaN(Number(value))) {
        data = Number(value);
      }
      return data;
    };

    const handleBlur =
      onBlur &&
      ((event: FocusEvent<HTMLInputElement>) => {
        const {
          target: { value },
        } = event;
        onBlur({ name, value: getValue(value) });
      });
    const handleChange =
      onChange &&
      ((event: ChangeEvent<HTMLInputElement>) => {
        const {
          target: { value, selectionStart },
        } = event;
        onChange({
          name,
          value: getValue(value),
          selectionStart: isNull(selectionStart) ? 0 : selectionStart,
        });
      });
    return <$Input {...props} name={name} onBlur={handleBlur} onChange={handleChange} ref={ref as any} />;
  }
});

const Input = memo(ForwardRefInput);

const StyledInput = styled(Input)`
  &.ant-input {
    box-shadow: none;
    border-radius: 4px;
    padding: 4px 11px;
    line-height: 1.5;

    &&::placeholder {
      color: #aaa;
    }
  }
`;

export const InputWithWrapper = styled(Input)`
  && {
    line-height: 1.5;
    padding: 0;
    border: 2px solid transparent;
    border-radius: 4px;
    box-shadow: 0 0 0 1px ${theme.colorsDisabledGreyButtons};
    height: 44px;
    font-size: ${theme.commonFontSize};
  }

  &&:hover,
  &&:focus-within {
    border-color: ${theme.colorsPrimaryBlue} !important;
    box-shadow: none;
  }

  && .ant-input {
    padding: 4px 11px 4px 0;
    line-height: 1.5 !important;
    height: 100%;
  }

  && .ant-input-prefix {
    padding-left: 11px;
  }

  &&::placeholder {
    color: #aaa;
  }
`;

export default StyledInput;
